/*
 * Search overlay - behaviour only.
 *
 * The overlay, the best-seller grid, every card and the variant sheet are
 * rendered by snippets/search-overlay.liquid; results come back as ready
 * markup from sections/search-overlay-results.liquid.
 *
 * What the original did that is gone from here: building the entire overlay
 * in JavaScript, fetching the best sellers, fetching /products/<handle>.js
 * once per card only to learn its price range, fetching it again on every
 * quick add, fetching two theme sections per query and scraping product cards
 * out of their HTML, and wrestling the header's search button away from the
 * theme with repeated selector sweeps and a MutationObserver.
 *
 * Ported from the Variant experience exp-EXPBB311D98.
 */
(function () {
  'use strict';

  var EXPERIMENT_ID = 'EXPBB311D98';
  var VARIATION_ID = '1';

  var RECENT_KEY = 'vr_recent_searches';
  var RECENT_LIMIT = 8;
  var MIN_SAVED_LENGTH = 2;
  var DEBOUNCE_MS = 300;
  var REVEAL_STEP = 10;

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  var overlay = document.querySelector('[data-search-overlay]');
  var sheet = document.querySelector('[data-search-sheet]');
  if (!overlay) return;

  var input = overlay.querySelector('[data-search-input]');
  var clearButton = overlay.querySelector('[data-search-clear]');
  var discovery = overlay.querySelector('[data-search-discovery]');
  var results = overlay.querySelector('[data-search-results]');
  var recentSection = overlay.querySelector('[data-search-recent-section]');
  var recentList = overlay.querySelector('[data-search-recent-list]');

  var influencedReported = false;

  function report() {
    if (influencedReported) return;
    influencedReported = true;
    try {
      if (typeof Variant === 'undefined' || !Variant.publishCustomEvent) return;
      Variant.publishCustomEvent('influenced-revenue', {
        experiment_id: EXPERIMENT_ID,
        variation_id: VARIATION_ID
      });
    } catch (e) {
      /* never break the page for analytics */
    }
  }

  // ---------------------------------------------------------------------------
  // Recent searches
  // ---------------------------------------------------------------------------
  function readRecent() {
    try {
      var stored = JSON.parse(localStorage.getItem(RECENT_KEY));
      if (!Array.isArray(stored)) return [];
      return stored.filter(function (term) {
        return term && term.length >= MIN_SAVED_LENGTH;
      });
    } catch (e) {
      return [];
    }
  }

  function rememberSearch(term) {
    if (!term || term.length < MIN_SAVED_LENGTH) return;
    try {
      var terms = readRecent().filter(function (existing) {
        return existing !== term;
      });
      terms.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(terms.slice(0, RECENT_LIMIT)));
    } catch (e) {
      /* private mode */
    }
  }

  function renderRecent() {
    if (!recentSection || !recentList) return;

    var terms = readRecent();
    recentSection.hidden = terms.length === 0;
    recentList.replaceChildren();

    terms.forEach(function (term) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'search-overlay__pill';
      pill.setAttribute('data-search-term', term);
      pill.textContent = term;
      recentList.appendChild(pill);
    });
  }

  // ---------------------------------------------------------------------------
  // Opening and closing
  // ---------------------------------------------------------------------------
  function open() {
    overlay.hidden = false;
    renderRecent();
    showDiscovery();

    // Two frames so the opening transition runs from the hidden state.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        input.focus();
      });
    });

    report();
  }

  function close() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    input.value = '';
    clearButton.hidden = true;
    clearTimeout(searchTimer);
    showDiscovery();

    setTimeout(function () {
      overlay.hidden = true;
    }, 400);
  }

  function showDiscovery() {
    discovery.hidden = false;
    results.hidden = true;
    results.replaceChildren();
  }

  function showResults() {
    discovery.hidden = true;
    results.hidden = false;
  }

  // The header's search button carries this; nothing has to be taken over.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-search-overlay-trigger]');
    if (!trigger) return;
    event.preventDefault();
    open();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    if (sheet && !sheet.hidden) {
      closeSheet();
      return;
    }
    if (!overlay.hidden) close();
  });

  overlay.addEventListener('click', function (event) {
    if (event.target === overlay || event.target.closest('[data-search-close]')) {
      close();
      return;
    }

    var pill = event.target.closest('[data-search-term]');
    if (pill) {
      runSearch(pill.getAttribute('data-search-term'), true);
      return;
    }

    if (event.target.closest('[data-search-recent-clear]')) {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch (e) {
        /* ignore */
      }
      renderRecent();
      return;
    }

    if (event.target.closest('[data-search-load-more]')) {
      revealMore(event.target.closest('[data-search-load-more]'));
      return;
    }

    var quickAdd = event.target.closest('[data-search-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      onQuickAdd(quickAdd);
    }
  });

  /** "Load more" only uncovers rows that are already on the page. */
  function revealMore(button) {
    var hiddenCells = overlay.querySelectorAll('[data-search-grid-cell][hidden]');
    Array.prototype.slice.call(hiddenCells, 0, REVEAL_STEP).forEach(function (cell) {
      cell.hidden = false;
    });

    if (overlay.querySelectorAll('[data-search-grid-cell][hidden]').length === 0) {
      button.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Searching
  // ---------------------------------------------------------------------------
  var searchTimer = null;
  var requestToken = 0;

  // Resolved by file name: the section belongs to no template, it exists only
  // to answer the Section Rendering API.
  var RESULTS_SECTION = 'search-overlay-results';

  input.addEventListener('input', function () {
    var query = input.value.trim();
    clearButton.hidden = query.length === 0;

    clearTimeout(searchTimer);
    if (query.length < 1) {
      showDiscovery();
      return;
    }

    searchTimer = setTimeout(function () {
      runSearch(query, false);
    }, DEBOUNCE_MS);
  });

  input.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    var query = input.value.trim();
    if (!query) return;
    rememberSearch(query);
    window.location.href =
      rootUrl() + 'search?type=product&q=' + encodeURIComponent(query);
  });

  clearButton.addEventListener('click', function () {
    input.value = '';
    clearButton.hidden = true;
    clearTimeout(searchTimer);
    showDiscovery();
    input.focus();
  });

  function runSearch(query, fromPill) {
    if (!query) return;

    if (fromPill) {
      input.value = query;
      clearButton.hidden = false;
    }

    showResults();
    results.innerHTML = '<p class="search-overlay__loading">מחפש...</p>';

    var token = ++requestToken;
    var url =
      rootUrl() +
      'search?type=product&options[prefix]=last&options[unavailable_products]=hide&q=' +
      encodeURIComponent(query) +
      '&section_id=' +
      RESULTS_SECTION;

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        // A slower earlier request must never overwrite a newer one.
        if (token !== requestToken) return;
        if (input.value.trim() !== query) return;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var markup = parsed.querySelector('[data-search-overlay-results]');
        if (!markup) {
          showDiscovery();
          return;
        }

        results.innerHTML = markup.innerHTML;
        rememberSearch(query);
      })
      .catch(function () {
        if (token === requestToken) showDiscovery();
      });
  }

  // ---------------------------------------------------------------------------
  // Quick add
  // ---------------------------------------------------------------------------
  function onQuickAdd(button) {
    var card = button.closest('[data-search-card]');
    if (!card) return;

    if (button.getAttribute('data-has-options') === 'true') {
      openSheet(card);
      return;
    }

    addToCart(button.getAttribute('data-variant-id'), button);
  }

  function addToCart(variantId, button) {
    var id = parseInt(variantId, 10);
    if (!id) return;

    if (button) button.disabled = true;

    var sections = ['cart-drawer', 'header']
      .map(function (name) {
        var element = document.querySelector('.shopify-section[id*="' + name + '"]');
        return element ? element.id.replace('shopify-section-', '') : null;
      })
      .filter(Boolean);

    var body = { items: [{ id: id, quantity: 1 }] };
    if (sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(rootUrl() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (json) {
        if (!json || json.status) throw new Error('add failed');

        if (json.sections) {
          Object.keys(json.sections).forEach(function (id2) {
            var html = json.sections[id2];
            if (html == null) return;
            var element = document.getElementById('shopify-section-' + id2);
            if (!element) return;
            element.outerHTML = html;
            document.dispatchEvent(
              new CustomEvent('shopify:section:load', { detail: { sectionId: id2 } })
            );
          });
        }

        closeSheet();
        close();

        requestAnimationFrame(function () {
          var drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.setAttribute('open', '');
        });
      })
      .catch(function () {
        /* the drawer will show the truth either way */
      })
      .then(function () {
        if (button) button.disabled = false;
      });
  }

  // ---------------------------------------------------------------------------
  // Variant sheet
  // ---------------------------------------------------------------------------
  var sheetImage = sheet && sheet.querySelector('[data-search-sheet-image]');
  var sheetTitle = sheet && sheet.querySelector('[data-search-sheet-title]');
  var sheetPrice = sheet && sheet.querySelector('[data-search-sheet-price]');
  var sheetOptions = sheet && sheet.querySelector('[data-search-sheet-options]');
  var sheetSubmit = sheet && sheet.querySelector('[data-search-sheet-submit]');

  function closeSheet() {
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    setTimeout(function () {
      sheet.hidden = true;
    }, 300);
  }

  if (sheet) {
    sheet.addEventListener('click', function (event) {
      if (event.target === sheet || event.target.closest('[data-search-sheet-close]')) closeSheet();
    });
  }

  function openSheet(card) {
    if (!sheet) return;

    var payload = card.querySelector('[data-search-card-variants]');
    if (!payload) return;

    var data;
    try {
      data = JSON.parse(payload.textContent);
    } catch (e) {
      return;
    }

    var selections = {};
    var firstAvailable = data.variants.filter(function (variant) {
      return variant.available;
    })[0];
    if (firstAvailable) {
      data.options.forEach(function (option, index) {
        selections[option.name] = firstAvailable.options[index];
      });
    }

    sheetImage.src = data.image || '';
    sheetImage.alt = data.title || '';
    sheetTitle.textContent = data.title || '';

    function matched() {
      return data.variants.filter(function (variant) {
        return data.options.every(function (option, index) {
          return selections[option.name] === variant.options[index];
        });
      })[0];
    }

    function render() {
      sheetOptions.replaceChildren();

      data.options.forEach(function (option, optionIndex) {
        var group = document.createElement('div');
        group.className = 'search-sheet__group';

        var label = document.createElement('span');
        label.className = 'search-sheet__label';
        label.textContent = option.name;
        group.appendChild(label);

        var values = document.createElement('div');
        values.className = 'search-sheet__values';

        option.values.forEach(function (value) {
          // A value is offered when some purchasable variant carries it
          // alongside everything else already picked.
          var reachable = data.variants.some(function (variant) {
            if (!variant.available) return false;
            if (variant.options[optionIndex] !== value) return false;
            return data.options.every(function (other, otherIndex) {
              if (otherIndex === optionIndex) return true;
              if (!selections[other.name]) return true;
              return variant.options[otherIndex] === selections[other.name];
            });
          });

          var swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.className = 'search-sheet__swatch';
          swatch.textContent = value;
          swatch.classList.toggle('is-selected', selections[option.name] === value);
          swatch.classList.toggle('is-unavailable', !reachable);
          swatch.addEventListener('click', function () {
            selections[option.name] = value;
            render();
          });

          values.appendChild(swatch);
        });

        group.appendChild(values);
        sheetOptions.appendChild(group);
      });

      var variant = matched();
      sheetPrice.replaceChildren();

      if (!variant) {
        sheetSubmit.disabled = true;
        sheetSubmit.textContent = 'בחרו אפשרות';
        return;
      }

      if (variant.image) sheetImage.src = variant.image;

      if (variant.compareAtPrice) {
        var was = document.createElement('span');
        was.className = 'search-sheet__price-old';
        was.textContent = variant.compareAtPrice;
        sheetPrice.appendChild(was);
      }

      var now = document.createElement('span');
      now.className = 'search-sheet__price';
      now.textContent = variant.price;
      sheetPrice.appendChild(now);

      sheetSubmit.disabled = !variant.available;
      sheetSubmit.textContent = variant.available ? 'הוספה לסל' : 'אזל';
    }

    render();

    sheetSubmit.onclick = function () {
      var variant = matched();
      if (!variant || !variant.available) return;
      sheetSubmit.disabled = true;
      sheetSubmit.textContent = 'מוסיף...';
      addToCart(variant.id, null);
    };

    sheet.hidden = false;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        sheet.classList.add('is-open');
      });
    });
  }
})();
